


import { useEffect, useState } from "react";
import "../../App.css";
import SelectAPIKey from "../../components/SelectAPIKey";
import { useNavigate } from "react-router-dom";
import Title from "../../components/Title";
import { RoutePages } from "../../ultils";
import { ACTIONS, EXECUTOR_WRAPPER, REASON_CODES } from "./specs/fb_action_specs";

const FB_ACTION_ENDPOINT = process.env.REACT_APP_EXECUTOR_API_URL + "/fb_action";
const defaultAccount = { cookie: "", ua: "" };
const defaultProxy = { host: "", port: "", username: "", password: "" };

function getDefaultParams(actionName) {
    const action = ACTIONS[actionName];
    if (!action) return {};
    const params = {};
    action.params.forEach(p => {
        if (p.type === "array") params[p.key] = [];
        else params[p.key] = "";
    });
    return params;
}

const FBActionDocs = () => {
    const [apiKey, setApiKey] = useState(null);
    const [account, setAccount] = useState(defaultAccount);
    const [isUseProxy, setIsUseProxy] = useState(true);
    const [proxy, setProxy] = useState(defaultProxy);
    const [actionName, setActionName] = useState("");
    const [params, setParams] = useState({});
    const [paramsMap, setParamsMap] = useState({});
    const [payloadStr, setPayloadStr] = useState("{}");
    const [payloadError, setPayloadError] = useState(null);
    const [response, setResponse] = useState(null);
    const [statusCode, setStatusCode] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        setAccount({ cookie: localStorage.getItem("fb_action_account_cookie") || "", ua: localStorage.getItem("fb_action_account_ua") || "" });
        setProxy({
            host: localStorage.getItem("fb_action_proxy_host") || "",
            port: localStorage.getItem("fb_action_proxy_port") || "",
            username: localStorage.getItem("fb_action_proxy_username") || "",
            password: localStorage.getItem("fb_action_proxy_password") || "",
        })
        try {
            setParams(JSON.parse(localStorage.getItem("fb_action_params")));
        } catch (e) {
            console.error("Failed to parse saved params:", e);
        }
    }, []);

    useEffect(() => {
        localStorage.setItem("fb_action_account_cookie", account.cookie);
        localStorage.setItem("fb_action_account_ua", account.ua);
        localStorage.setItem("fb_action_proxy_host", proxy.host);
        localStorage.setItem("fb_action_proxy_port", proxy.port);
        localStorage.setItem("fb_action_proxy_username", proxy.username);
        localStorage.setItem("fb_action_proxy_password", proxy.password);
    }, [account, proxy]);

    useEffect(() => {
        localStorage.setItem("fb_action_params", JSON.stringify(params));
    }, [params]);

    // Update params when action changes
    useEffect(() => {
        setParams(paramsMap[actionName] || getDefaultParams(actionName));
    }, [actionName, paramsMap]);

    // Construct payload JSON string
    useEffect(() => {
        const payload = {
            account,
            proxy: isUseProxy ? proxy : null,
            action: {
                name: actionName,
                params,
            },
        };
        setPayloadStr(JSON.stringify(payload, null, 2));
    }, [account, isUseProxy, proxy, actionName, params]);

    useEffect(() => {
        try {
            JSON.parse(payloadStr);
            setPayloadError(null);
        } catch (e) {
            setPayloadError(e.message);
        }
    }, [payloadStr]);

    // Handle submit
    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setResponse(null);
        setStatusCode(null);
        setError(null);
        try {
            const res = await fetch(FB_ACTION_ENDPOINT + "?access_token=" + apiKey, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: payloadStr,
            });
            setStatusCode(res.status);
            const data = await res.text();
            try {
                var jsonData = JSON.parse(data);
                setResponse(jsonData);
            }
            catch {
                setResponse(data);
            }
        } catch (err) {
            setError(err.message);
        }
        setLoading(false);
    };

    // UI helpers
    const actionOptions = Object.values(ACTIONS);
    const actionDef = ACTIONS[actionName];
    const curlCommand = `curl -X POST "${FB_ACTION_ENDPOINT}?access_token=${apiKey}" -H "Content-Type: application/json" -d "${payloadStr.replace(/"/g, '\\"')}"`

    // Styling: use Tailwind classes for consistency with the rest of the frontend
    return (
        <div className="w-full px-2 py-2 bg-white rounded shadow">
            <Title page={RoutePages.DOCS.FB_ACTION} title="Docs FB-Actions" />
            {/* Brief description */}
            <div className="flex flex-col mb-4 bg-white p-4 sm:p-6 rounded-2xl shadow-lg border max-w-full mx-auto animate-fade-in">
                <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-2">
                    <h2 className="w-full sm:w-2/3 text-2xl sm:text-3xl font-extrabold text-center sm:text-left text-blue-700 tracking-wide">
                        Facebook Action API
                    </h2>
                    <div className="w-full sm:w-1/3 flex justify-center sm:justify-end gap-3">
                        <button onClick={() => navigate("/pricing/fb_action")} className="w-full sm:w-auto px-4 py-2 text-white text-base font-semibold rounded-lg bg-gradient-to-r from-green-400 to-green-600 hover:from-green-500 hover:to-green-700 shadow transition-all duration-200">
                            Free Trial
                        </button>
                        <button onClick={() => navigate("/pricing/fb_action")} className="w-full sm:w-auto px-4 py-2 text-white text-base font-semibold rounded-lg bg-gradient-to-r from-blue-400 to-blue-600 hover:from-blue-500 hover:to-blue-700 shadow transition-all duration-200">
                            Buy
                        </button>
                    </div>
                </div>
                <div className="flex flex-col mb-2">
                    <p className="text-gray-700 text-base text-begin max-w-3xl">
                        <b>Manage account</b> via HTTP API. <b>Proxy configurable</b> - keep your account safe!<br />
                        <b>Actions: </b>Post to feed, groups, comment, change profile picture, name, bio, ...<br />
                        Just provide your <b>Cookie</b> and <b>User-Agent</b>, then <b>call the action</b> you want!
                    </p>
                </div>
            </div>
            {/* API Explorer Tool */}
            <div className="flex flex-col mb-4 bg-white p-4 sm:p-6 rounded-2xl shadow-lg border max-w-full mx-auto animate-fade-in">
                <div className="flex flex-row gap-8">
                    {/* Left column: Forms */}
                    <div className="flex flex-col w-3/5">
                        {/* Input or select API Key */}
                        <div className="mb-6">
                            <h2 className="font-semibold text-xl mb-2">1. API Key</h2>
                            <div className="flex flex-row gap-2">
                                <input
                                    className="w-full border rounded px-3 py-2 focus:outline-none focus:ring focus:border-blue-400"
                                    placeholder="Enter your API key..."
                                    type="text"
                                    value={apiKey}
                                    onChange={e => setApiKey(e.target.value)}
                                />
                                <SelectAPIKey className="w-32" selectedKey={apiKey} onSelect={setApiKey} />
                            </div>
                        </div>

                        {/* Account form */}
                        <div className="mb-6 border-t pt-4">
                            <h2 className="font-semibold text-xl mb-2">2. Facebook Account</h2>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-sm font-medium mb-1">Cookie <span className="text-red-500">*</span></label>
                                    <input
                                        className="w-full border rounded px-3 py-2 focus:outline-none focus:ring focus:border-blue-400"
                                        type="text"
                                        value={account.cookie}
                                        onChange={e => setAccount({ ...account, cookie: e.target.value })}
                                        placeholder="Paste Facebook cookie..."
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">User Agent</label>
                                    <input
                                        className="w-full border rounded px-3 py-2 focus:outline-none focus:ring focus:border-blue-400"
                                        type="text"
                                        value={account.ua}
                                        onChange={e => setAccount({ ...account, ua: e.target.value })}
                                        placeholder="User Agent (optional)"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">2FA</label>
                                    <input
                                        className="w-full border rounded px-3 py-2 focus:outline-none focus:ring focus:border-blue-400"
                                        type="text"
                                        value={account.code2fa}
                                        onChange={e => setAccount({ ...account, code2fa: e.target.value })}
                                        placeholder="2FA Code (optional)"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Proxy form */}
                        <div className="mb-6 border-t pt-4">
                            <div className="flex items-center mb-2">
                                <h2 className="font-semibold text-xl mr-2">3. Use Proxy</h2>
                                <label className="flex items-center cursor-pointer">
                                    <input
                                        type="checkbox"
                                        className="form-checkbox h-5 w-5 text-blue-600"
                                        checked={isUseProxy}
                                        onChange={e => setIsUseProxy(e.target.checked)}
                                    />
                                </label>
                            </div>
                            {isUseProxy && (
                                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium mb-1">Host</label>
                                        <input
                                            className="w-full border rounded px-3 py-2 focus:outline-none focus:ring focus:border-blue-400"
                                            type="text"
                                            value={proxy.host}
                                            onChange={e => setProxy({ ...proxy, host: e.target.value })}
                                            placeholder="Proxy host"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium mb-1">Port</label>
                                        <input
                                            className="w-full border rounded px-3 py-2 focus:outline-none focus:ring focus:border-blue-400"
                                            type="text"
                                            value={proxy.port}
                                            onChange={e => setProxy({ ...proxy, port: e.target.value })}
                                            placeholder="Proxy port"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium mb-1">Username</label>
                                        <input
                                            className="w-full border rounded px-3 py-2 focus:outline-none focus:ring focus:border-blue-400"
                                            type="text"
                                            value={proxy.username}
                                            onChange={e => setProxy({ ...proxy, username: e.target.value })}
                                            placeholder="Proxy username"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium mb-1">Password</label>
                                        <input
                                            className="w-full border rounded px-3 py-2 focus:outline-none focus:ring focus:border-blue-400"
                                            type="text"
                                            value={proxy.password}
                                            onChange={e => setProxy({ ...proxy, password: e.target.value })}
                                            placeholder="Proxy password"
                                        />
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Action select & params */}
                        <div className="mb-6 border-t pt-4">
                            <h2 className="font-semibold text-xl mb-2">4. Select Action</h2>
                            <select
                                className="w-full border rounded px-3 py-2 mb-4 focus:outline-none focus:ring focus:border-blue-400"
                                value={actionName}
                                onChange={e => setActionName(e.target.value)}
                            >
                                <option value="">-- Select action --</option>
                                {actionOptions.map(a => (
                                    <option key={a.name} value={a.name}>{a.name} | {a.doc}</option>
                                ))}
                            </select>

                            {actionDef && (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {actionDef.params.map(param => (
                                        <div key={param.key}>
                                            <label className="block font-medium mb-1">
                                                {param.key} {param.required && <span className="text-red-500">*</span>}
                                            </label>
                                            {param.type === "array" ? (
                                                <input
                                                    className="w-full border rounded px-3 py-2 focus:outline-none focus:ring focus:border-blue-400"
                                                    type="text"
                                                    value={params[param.key]?.join ? params[param.key].join(",") : ""}
                                                    onChange={e => {
                                                        const newParams = { ...params, [param.key]: e.target.value.split(",").map(s => s) };
                                                        setParams(newParams);
                                                        setParamsMap(map => ({ ...map, [actionName]: newParams }));
                                                    }}
                                                    placeholder={param.doc}
                                                />
                                            ) : (
                                                <input
                                                    className="w-full border rounded px-3 py-2 focus:outline-none focus:ring focus:border-blue-400"
                                                    type="text"
                                                    value={params[param.key] || ""}
                                                    onChange={e => {
                                                        const newParams = { ...params, [param.key]: e.target.value };
                                                        setParams(newParams);
                                                        setParamsMap(map => ({ ...map, [actionName]: newParams }));
                                                    }}
                                                    placeholder={param.doc}
                                                />
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}


                            {actionDef && (
                                <div className="flex flex-row w-full">
                                    <div className="w-full mt-4 p-4 bg-gray-50 border-l-4 border-blue-500">
                                        <h3 className="font-semibold mb-2">Success Response</h3>
                                        <textarea
                                            className="w-full border rounded px-3 py-2 font-mono text-sm focus:outline-none focus:ring focus:border-blue-400 min-h-[120px] flex-1"
                                            value={JSON.stringify(EXECUTOR_WRAPPER(actionDef.response), null, 2)}
                                            readOnly={true}
                                            style={{ minHeight: 250 }}
                                        />
                                    </div>
                                    <div className="mt-4 p-4 bg-yellow-50 border-l-4 border-yellow-500" style={{ minWidth: 410 }}>
                                        <h3 className="font-semibold mb-2 text-yellow-700">Possible Status Codes</h3>
                                        <ul className="list-disc pl-6">
                                            {actionDef.posible_code.map((code, idx) => (
                                                <li className="mt-2" key={code + '-' + idx}>
                                                    <span className="font-mono font-semibold">{code}</span>
                                                    {typeof code === 'number' &&
                                                        <>: <span className="text-gray-700">{Object.keys(REASON_CODES).find(k => REASON_CODES[k] === code)}</span></>
                                                    }
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                            )}


                        </div>
                    </div>

                    {/* Right column: JSON, submit, response */}
                    <div className="flex flex-col w-2/5">
                        {/* Payload JSON textarea */}
                        <div className="mb-6 flex flex-col">
                            <h2 className="font-semibold text-lg mb-2">5. Payload JSON {!payloadError ? "" : <span className="text-red-500 italic">*{payloadError}</span>}</h2>
                            <textarea
                                className={"w-full border rounded font-mono px-3 py-2 text-sm focus:outline-none focus:ring focus:border-blue-400" + (!payloadError ? "" : " bg-red-100")}
                                value={payloadStr}
                                onChange={(e) => setPayloadStr(e.target.value)}
                                style={{ minHeight: 300, maxHeight: 400 }}
                            />
                        </div>


                        {/* CURL command */}
                        <div className="mb-6 flex flex-col">
                            <div className="flex gap-2">
                                <h2 className="font-semibold text-lg mb-2">6. CURL Command</h2>
                                <p className="italic underline text-base text-gray-500 hover:cursor-pointer" onClick={() => { window.open("https://curlconverter.com/", "_blank") }}>*Get code JS, Python, PHP, ...*</p>
                            </div>
                            <p className="font-mono border p-2 bg-gray-100 text-sm break-all">{curlCommand}</p>
                        </div>

                        {/* Submit button */}
                        <div className="mb-6 flex flex-col">
                            <h2 className="font-semibold text-lg mb-2">7. Send request</h2>
                            <button
                                className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-2 rounded shadow disabled:opacity-60 w-full"
                                onClick={handleSubmit}
                                disabled={loading}
                            >
                                {loading ? "Sending..." : "Send request"}
                            </button>
                        </div>

                        {/* Response */}
                        <div className="mb-6 flex flex-col">
                            <h2 className="font-semibold text-lg mb-2">8. Response {statusCode !== null && <span className="text-gray-600">status_code -&gt; ({statusCode})</span>}</h2>
                            <textarea
                                className="w-full border rounded px-3 py-2 font-mono text-sm bg-gray-100 min-h-[120px] flex-1"
                                value={response ? JSON.stringify(response, null, 2) : ""}
                                readOnly
                                style={{ minHeight: 180 }}
                            />
                            {error && <p className="text-red-500 mt-2">Error: {error}</p>}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FBActionDocs;